import type {ReactElement} from "react";
import React from "react";
import type {DesignCenterTabProps} from "../Interfaces.tsx";
import type {
    UUID,
    Variable,
    Waterfall,
    WaterfallAvailableFundsRow,
    WaterfallItem,
    WaterfallPaymentPriorityRow,
} from "../../base/ui_general/GeneralInterfaces.tsx";
import {RBSCard, RBSCardVariants} from "../../base/rbs_components/RBSCard";
import {RBSButton, RBSButtonSizes, RBSButtonVariants} from "../../base/rbs_components/RBSButton";
import {Accordion, Col, Row} from "react-bootstrap";
import autobind from "autobind-decorator";
import {BaseComponent, type BaseComponentState} from "../../base/ui_general/BaseComponent.tsx";
import {WaterfallEditModal} from "../modals/WaterfallEditModal.tsx";
import {VariablesBadges} from "../../base/ui_general/GeneralFunctionComponents.tsx";
import {InputStateControl} from "../../base/utils/InputStateControl.tsx";
import {
    ApiEndPoints,
    UserLanguages,
    LabelTextMessageTypes,
    TriggerConditionEnum,
    ValidationSchemes,
    WaterfallItemConceptEnum,
    WaterfallProcessableTypeEnum
} from "../../base/Settings.tsx";
import {
    getRequest_create_waterfall_available_funds_row,
    getRequest_delete_waterfall_available_funds_row,
    getRequest_update_waterfall_available_funds_row,
} from "../../api/waterfall_available_funds_row.tsx";
import {createSuccessFeedbackMessage} from "../../base/utils/GeneralUtilities.tsx";
import {
    getRequest_create_waterfall,
    getRequest_delete_waterfall,
    getRequest_update_waterfall
} from "../../api/waterfall.tsx";
import {
    getRequest_create_waterfall_payment_priority_row,
    getRequest_delete_waterfall_payment_priority_row,
    getRequest_update_waterfall_payment_priority_row
} from "../../api/waterfall_payment_priority_row.tsx";
import styles from './TabShared.module.css';
import {TabCardHeader} from './components/TabCardHeader';
import {VariableDropCell} from './components/VariableDropCell';
import {EmptyStateRow} from './components/EmptyStateRow';


const triggerConditionLabels: Record<TriggerConditionEnum, string> = {
    [TriggerConditionEnum.RUN_IF]: "RUN IF",
    [TriggerConditionEnum.RUN_IF_NOT]: "RUN IF NOT"
};

/** WaterfallAccordionProps */
interface WaterfallAccordionProps {
    user_language: UserLanguages;
    availableFundsRows: WaterfallAvailableFundsRow[],
    variables: Variable[],
    waterfallId: string,
    paymentPriorityRows: WaterfallPaymentPriorityRow[],
    waterfallItems: WaterfallItem[],
    trigger_condition?: TriggerConditionEnum,
    trigger_variable_id?: string,
    onChangeTriggerCondition: (newValue: TriggerConditionEnum) => void,
    onChangeTriggerVariable: (newVariableId?: string) => void,
    onDropVariableToFundsRow: (fundsRowId: string, variableId: string) => void,
    onDropVariableToNewFundsRow: (variableId: string) => void,
    onAddFundsRow: () => void,
    onAddPriorityRow: () => void,
    rowInputStateControl: InputStateControl,
    rowInputStateIds: Record<string, { variableId?: number; waterfallItemId?: number }>;
    onChangePaymentPriority: (rowId: string, newPriority: number) => void;
    onDeleteAvailableFundsRow: (rowId: string) => void;
    onDeletePaymentPriorityRow: (rowId: string) => void;
    onChangeWaterfallItemConcept: (waterfallId: string, rowId: string, concept: WaterfallItemConceptEnum) => void;
    onDropWaterfallItemToRow?: (
        waterfallId: string,
        rowId: string,
        itemId: string
    ) => void;
}

const WaterfallAccordion: React.FC<WaterfallAccordionProps> = ({
                                                                   user_language,
                                                                   availableFundsRows,
                                                                   variables,
                                                                   waterfallId,
                                                                   paymentPriorityRows,
                                                                   waterfallItems,
                                                                   trigger_condition,
                                                                   trigger_variable_id,
                                                                   onChangeTriggerCondition,
                                                                   onChangeTriggerVariable,
                                                                   onDropVariableToFundsRow,
                                                                   onDropVariableToNewFundsRow,
                                                                   onAddFundsRow,
                                                                   onAddPriorityRow,
                                                                   rowInputStateControl,
                                                                   rowInputStateIds,
                                                                   onChangePaymentPriority,
                                                                   onDeleteAvailableFundsRow,
                                                                   onDeletePaymentPriorityRow,
                                                                   onChangeWaterfallItemConcept,
                                                                   onDropWaterfallItemToRow
                                                               }) => {
    // Add hoveredFundsRow state for row hover effect
    const [hoveredFundsRow, setHoveredFundsRow] = React.useState<string | null>(null);
    // Add hoveredPriorityRow state for Priority of Payments table
    const [hoveredPriorityRow, setHoveredPriorityRow] = React.useState<string | null>(null);

    /** --- Handlers for Priority of Payments --- */
    const handlePaymentPriorityRowDrop = (rowId: string) => {
        return (e: React.DragEvent<HTMLTableCellElement>) => {
            e.preventDefault();
            const itemName = e.dataTransfer.getData("item");
            if (!itemName) return;
            const wfId = waterfallId;
            const item = waterfallItems.find((i) => i.name === itemName);
            if (!item) return;
            onDropWaterfallItemToRow?.(wfId, rowId, item.id);
        };
    };

    const handlePaymentPriorityItemDragStart = (
        item: WaterfallItem,
        rowId: string,
        e: React.DragEvent<HTMLSpanElement>
    ) => {
        e.dataTransfer.setData("item", item.name);
        e.dataTransfer.setData("wfId", waterfallId);
        e.dataTransfer.setData("section", "PriorityOfPayments");
        e.dataTransfer.setData("rowId", rowId);
        e.dataTransfer.setData("from", "PriorityOfPayments");
    };

    /** --- Handlers for Available Funds --- */
    const handleFundsDropNew = (e: React.DragEvent<HTMLTableCellElement>) => {
        e.preventDefault();
        const variableName = e.dataTransfer.getData("variable");
        if (!variableName) return;
        const variable = variables.find((v) => v.name === variableName);
        if (variable) onDropVariableToNewFundsRow(variable.id);
    };

    const handleFundsRowDrop =
        (fundsRowId: string) => (e: React.DragEvent<HTMLTableCellElement>) => {
            e.preventDefault();
            const variableName = e.dataTransfer.getData("variable");
            if (!variableName) return;
            const variable = variables.find((v) => v.name === variableName);
            if (variable) onDropVariableToFundsRow(fundsRowId, variable.id);
        };

    /** --- Trigger Variable --- */
    const triggerVariable = variables?.find((v) => v.id === trigger_variable_id);

    const handleTriggerVariableDrop = (e: React.DragEvent<HTMLTableCellElement>) => {
        e.preventDefault();

        const variableName = e.dataTransfer.getData("variable");
        const from = e.dataTransfer.getData("from"); // "TriggersTab" | "VariablesTab"
        const fromTriggerId = e.dataTransfer.getData("fromTriggerId");

        // Remove variable from Trigger
        if (from === "VariablesTab" && fromTriggerId) {
            onChangeTriggerVariable(undefined);
            onChangeTriggerCondition(TriggerConditionEnum.RUN_IF);
            return;
        }

        // Only accept variables with type TRIGGER
        if (!variableName) return;
        const variable = variables.find((v) => v.name === variableName && v.type === "TRIGGER");
        if (!variable) {
            return;
        }

        onChangeTriggerVariable(variable.id);
    };

    const handleTriggerVariableDragStart = (v: Variable, e: React.DragEvent<Element>) => {
        e.dataTransfer.setData("variable", v.name);
        e.dataTransfer.setData("from", "TriggersTab");
        e.dataTransfer.setData("fromTriggerId", waterfallId);
    };

    /** --- Render Accordion --- */
    return (
        <Accordion>
            {/* Available Funds */}
            <Accordion.Item eventKey="0">
                <Accordion.Header>Available Funds</Accordion.Header>
                <Accordion.Body>
                    <table className={`${styles.dataTable} ${styles.tableMarginLg}`}>
                        <thead>
                        <tr>
                            <th className={styles.headerPadLg}>
                                Variable
                            </th>
                            <th className={`${styles.headerActionsNarrow} ${styles.headerPadLg}`}>
                                <RBSButton
                                    label="Add Row"
                                    variant={RBSButtonVariants.SECONDARY}
                                    size={RBSButtonSizes.SMALL}
                                    onClick={onAddFundsRow}
                                />
                            </th>
                        </tr>
                        </thead>
                        <tbody>
                        {availableFundsRows.length === 0 && (
                            <tr>
                                <VariableDropCell user_language={user_language} colSpan={2} onDrop={handleFundsDropNew} />
                            </tr>
                        )}
                        {availableFundsRows.map((row, idx) => {
                            const variable = variables?.find((v) => v.id === row.fund_variable_id);
                            const inputIds = rowInputStateIds[row.id];
                            const isRequiredMissing = inputIds && rowInputStateControl.getValidityForKey(`${row.id}-variable`) === false;
                            const isLastRow = idx === availableFundsRows.length - 1;

                            return (
                                <tr
                                    key={row.id}
                                    className={hoveredFundsRow === row.id ? styles.tableRowHover : ''}
                                    onMouseEnter={() => setHoveredFundsRow(row.id)}
                                    onMouseLeave={() => setHoveredFundsRow(null)}
                                >
                                    <VariableDropCell
                                        user_language={user_language}
                                        onDrop={handleFundsRowDrop(row.id)}
                                        isRequiredMissing={isRequiredMissing}
                                        isLastRow={isLastRow}
                                        minWidthPx={120}
                                    >
                                        {variable ? (
                                            <VariablesBadges
                                                variables={[variable]}
                                                draggable={true}
                                                onDragStart={(v, e) => e.dataTransfer.setData('variable', v.name)}
                                            />
                                        ) : undefined}
                                     </VariableDropCell>
                                    <td className={`${styles.actionCell} ${styles.cellPadLg} ${styles.cellNoBorderBetweenCols} ${!isLastRow ? styles.cellBorderBottom : ''}`}>
                                        <div className={styles.hoverButtons}>
                                            <RBSButton
                                                label="🗑️"
                                                title="Delete"
                                                onClick={() => onDeleteAvailableFundsRow(row.id)}
                                                variant={RBSButtonVariants.DANGER}
                                                size={RBSButtonSizes.SMALL}
                                            />
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                        </tbody>
                    </table>
                </Accordion.Body>
            </Accordion.Item>

            {/* Waterfall Condition */}
            <Accordion.Item eventKey="1">
                <Accordion.Header>Waterfall Condition</Accordion.Header>
                <Accordion.Body>
                    <table className={`${styles.dataTable} ${styles.tableMarginLg}`}>
                        <thead>
                        <tr>
                            <th className={`${styles.headerPadLg} ${styles.colNarrowSm}`}>Condition</th>
                            <th className={`${styles.headerPadLg} ${styles.colWide}`}>Variable</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr>
                            <td className={`${styles.cellPadLg} ${styles.colNarrowSm}`}>
                                <select
                                    className="form-select"
                                    value={triggerVariable ? (trigger_condition ?? TriggerConditionEnum.RUN_IF) : TriggerConditionEnum.RUN_IF}
                                    disabled={!triggerVariable}
                                    onChange={(e) => {
                                        const v = e.target.value as TriggerConditionEnum;
                                        if (v) onChangeTriggerCondition(v);
                                    }}
                                >
                                    {Object.values(TriggerConditionEnum).map((val) => (
                                        <option value={val} key={val}>
                                            {triggerConditionLabels[val]}
                                        </option>
                                    ))}
                                </select>
                            </td>
                            <VariableDropCell
                                user_language={user_language}
                                onDrop={handleTriggerVariableDrop}
                                minWidthPx={120}
                            >
                                {triggerVariable ? (
                                    <VariablesBadges
                                        variables={[triggerVariable]}
                                        draggable={true}
                                        onDragStart={handleTriggerVariableDragStart}
                                    />
                                ) : undefined}
                            </VariableDropCell>
                        </tr>
                        </tbody>
                    </table>
                </Accordion.Body>
            </Accordion.Item>

            {/* Priority of Payments */}
            <Accordion.Item eventKey="2">
                <Accordion.Header>Priority of Payments</Accordion.Header>
                <Accordion.Body>
                    <table className={`${styles.dataTable} ${styles.tableMarginLg}`}>
                        <thead>
                        <tr>
                            <th className={styles.headerPadLg}>Waterfall Item</th>
                            <th className={`${styles.headerPadLg} ${styles.colMedium}`}>Concept</th>
                            <th className={`${styles.headerPadLg} ${styles.colNarrowSm}`}>Priority</th>
                            <th className={`${styles.headerActionsNarrow} ${styles.headerPadLg}`}>
                                <RBSButton
                                    label="Add Row"
                                    variant={RBSButtonVariants.SECONDARY}
                                    size={RBSButtonSizes.SMALL}
                                    onClick={onAddPriorityRow}
                                />
                            </th>
                        </tr>
                        </thead>
                        <tbody>
                        {paymentPriorityRows.length === 0 && (
                            <EmptyStateRow inTable colSpan={4} message={'No Priority of Payments rows.'}/>
                        )}

                        {paymentPriorityRows.map((row, row_index) => {
                            const item = waterfallItems.find(wi => wi.id === row.waterfall_item_id);
                            const inputIds = rowInputStateIds[row.id];

                            const isRequiredMissing =
                                inputIds?.waterfallItemId &&
                                rowInputStateControl.getValidityForKey(`${row.id}-waterfallItem`) === false;

                            return (
                                <tr
                                    key={row.id}
                                    className={hoveredPriorityRow === row.id ? styles.tableRowHover : ''}
                                    onMouseEnter={() => setHoveredPriorityRow(row.id)}
                                    onMouseLeave={() => setHoveredPriorityRow(null)}
                                >
                                    <td
                                        onDrop={handlePaymentPriorityRowDrop(row.id)}
                                        onDragOver={e => e.preventDefault()}
                                        className={`${styles.cellCenter} ${styles.cellPadLg} ${isRequiredMissing ? styles.dropCellRequired : ''}`}
                                    >
                                        {item ? (
                                            <span
                                                draggable
                                                onDragStart={e => handlePaymentPriorityItemDragStart(item, row.id, e)}
                                                className={styles.draggableChip}
                                            >
                                                 {item.name}
                                             </span>
                                        ) : (
                                            <span className="text-muted">Drop Waterfall Item here</span>
                                        )}
                                    </td>

                                    {/* Concept dropdown */}
                                    <td className={styles.cellPadLg}>
                                        <select
                                            className="form-select"
                                            value={row.concept ?? ""}
                                            onChange={(e) => {
                                                const newConcept = e.target.value as WaterfallItemConceptEnum;
                                                onChangeWaterfallItemConcept(waterfallId, row.id, newConcept);
                                            }}
                                        >
                                            {Object.values(WaterfallItemConceptEnum).map((concept) => (
                                                <option key={concept} value={concept}>{concept}</option>
                                            ))}
                                        </select>
                                    </td>

                                    {/* Priority input */}
                                    <td className={styles.cellPadLg}>
                                        <input
                                            type="number"
                                            value={row.priority}
                                            onChange={(e) => {
                                                let lastPrio = paymentPriorityRows[0].priority;
                                                paymentPriorityRows.forEach((nest_row, nest_index) => {
                                                    if (nest_index >= row_index) {
                                                        const newPriorityEntered = nest_index === row_index ? parseInt(e.target.value, 10) : paymentPriorityRows[nest_index].priority;
                                                        const allowedPriorityLow = nest_index === 0 ? 1 : lastPrio;
                                                        const allowedPriorityHigh = nest_index === 0 ? 1 : lastPrio + 1;
                                                        const newPriority = Math.min(Math.max(allowedPriorityLow, newPriorityEntered), allowedPriorityHigh);
                                                        onChangePaymentPriority(nest_row.id, newPriority);
                                                        lastPrio = newPriority;
                                                    } else {
                                                        lastPrio = paymentPriorityRows[nest_index].priority;
                                                    }
                                                })
                                            }}
                                            className={styles.numericInputSmall}
                                        />
                                    </td>

                                    {/* Delete button column */}
                                    <td className={`${styles.actionCell} ${styles.cellPadLg}`}>
                                        <div className={styles.hoverButtons}>
                                            <RBSButton
                                                label="🗑️"
                                                title="Delete"
                                                onClick={() => onDeletePaymentPriorityRow(row.id)}
                                                variant={RBSButtonVariants.DANGER}
                                                size={RBSButtonSizes.SMALL}
                                            />
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                        </tbody>
                    </table>
                </Accordion.Body>
            </Accordion.Item>

        </Accordion>
    );
};


/** --- WaterfallsTab Class --- */
interface WaterfallsTabProps extends DesignCenterTabProps {
    waterfalls: Waterfall[];
    variables: Variable[];
    waterfall_items: WaterfallItem[];
}


interface WaterfallsTabState extends BaseComponentState {
    localWaterfalls: Waterfall[];
    nextRowId: number;
    nextWaterfallId: number;
    showEditWaterfallModal: boolean;
    editingWaterfall?: Waterfall;
    editingWaterfallValid: boolean;
    rowInputStateIds: Record<string, { variableId?: number; waterfallItemId?: number }>;
}

export class WaterfallsTab extends BaseComponent<WaterfallsTabProps, WaterfallsTabState> {
    private rowInputStateControl: InputStateControl = new InputStateControl();
    private editingWaterfallInputControl: InputStateControl = new InputStateControl();


    constructor(props: WaterfallsTabProps) {
        super(props);
        this.editingWaterfallInputControl.setGlobalValidityUpdateCallback(
            (isValid: boolean) => this.setState({editingWaterfallValid: isValid})
        );
        this.state = {
            ...this.state,
            localWaterfalls: props.waterfalls || [],
            nextRowId: 1,
            nextWaterfallId: 1,
            showEditWaterfallModal: false,
            editingWaterfall: undefined,
            editingWaterfallValid: true,
            rowInputStateIds: {},
        };
    }

    componentDidUpdate(prevProps: WaterfallsTabProps) {
        if (prevProps.waterfalls !== this.props.waterfalls) {
            this.setState({localWaterfalls: this.props.waterfalls || []}, () => {

                this.state.localWaterfalls.forEach(waterfall => {
                    waterfall.waterfall_payment_priority_rows?.forEach(row => {
                        this.registerPriorityRowInputs(row);
                    });
                });

                this.state.localWaterfalls.forEach(waterfall => {
                    waterfall.waterfall_available_funds_rows?.forEach(row => {
                        this.registerRowInputs(row);
                    });
                });
            });
        }
    }

    private registerRowInputs(row: WaterfallAvailableFundsRow) {
        const rowKey = row.id;
        if (!this.state.rowInputStateIds[rowKey]) {
            const variableId = this.rowInputStateControl.registerComponent(rowKey + "-variable", [ValidationSchemes.REQUIRED]);
            this.rowInputStateControl.updateValue(variableId, row.fund_variable_id || "");
            this.setState((state) => ({
                rowInputStateIds: {...state.rowInputStateIds, [rowKey]: {variableId}},
            }));
        }
    }

    private registerPriorityRowInputs(row: WaterfallPaymentPriorityRow) {
        const rowKey = row.id;
        if (!this.state.rowInputStateIds[rowKey]) {
            const waterfallItemId = this.rowInputStateControl.registerComponent(
                rowKey + "-waterfallItem",
                [ValidationSchemes.REQUIRED]
            );
            this.rowInputStateControl.updateValue(waterfallItemId, row.waterfall_item_id || "");
            this.setState((state) => ({
                rowInputStateIds: {
                    ...state.rowInputStateIds,
                    [rowKey]: {...state.rowInputStateIds[rowKey], waterfallItemId}
                }
            }));
        }
    }


    @autobind
    handleRemoveWaterfallItemFromRow(waterfallId: string, rowId: string) {
        const {localWaterfalls} = this.state;
        const updatedWaterfalls = localWaterfalls.map(w =>
            w.id === waterfallId
                ? {
                    ...w,
                    waterfall_payment_priority_rows: w.waterfall_payment_priority_rows.map(r =>
                        r.id === rowId ? {...r, waterfall_item_id: ""} : r
                    )
                }
                : w
        );
        this.setState({localWaterfalls: updatedWaterfalls}, () => {
            const inputIds = this.state.rowInputStateIds[rowId];
            if (inputIds?.waterfallItemId) {
                this.rowInputStateControl.updateValue(inputIds.waterfallItemId, "");
            }
        });
    }


    @autobind
    handleChangeWaterfallItemConcept(waterfallId: string, rowId: string, concept: WaterfallItemConceptEnum) {
        const {localWaterfalls} = this.state;
        const updatedWaterfalls = localWaterfalls.map((w) =>
            w.id === waterfallId
                ? {
                    ...w,
                    waterfall_payment_priority_rows: w.waterfall_payment_priority_rows.map((r) =>
                        r.id === rowId ? {...r, concept} : r
                    ),
                }
                : w
        );
        this.setState({localWaterfalls: updatedWaterfalls}, () => {

            const row = updatedWaterfalls
                .find((w) => w.id === waterfallId)!
                .waterfall_payment_priority_rows.find((r) => r.id === rowId)!;
            this.processFetchRequest(
                getRequest_update_waterfall_payment_priority_row(row),
                (updatedRow: WaterfallPaymentPriorityRow) => {
                    const newLocalWaterfalls = this.state.localWaterfalls.map((w) =>
                        w.id === waterfallId
                            ? {
                                ...w,
                                waterfall_payment_priority_rows: w.waterfall_payment_priority_rows.map((r) =>
                                    r.id === rowId ? updatedRow : r
                                ),
                            }
                            : w
                    );
                    this.setState({localWaterfalls: newLocalWaterfalls});
                    this.props.loadData([ApiEndPoints.WATERFALL, ApiEndPoints.VARIABLE]);
                }
            );
        });
    }

    @autobind
    async handleAddPriorityRow(waterfallId: string) {
        const {localWaterfalls, nextRowId} = this.state;
        const parentWaterfall = localWaterfalls.find(w => w.id === waterfallId);
        if (!parentWaterfall) return;

        const tempId = `newpriority-${nextRowId}`;
        const priorities = parentWaterfall.waterfall_payment_priority_rows?.map(r => r.priority ?? 0) || [];
        const maxPriority = priorities.length > 0 ? Math.max(...priorities) : 0;

        const orders = parentWaterfall.waterfall_payment_priority_rows?.map(r => r.processing_order ?? 0) || [];
        const maxOrder = orders.length > 0 ? Math.max(...orders) : -1;

        const newRow: WaterfallPaymentPriorityRow = {
            id: tempId,
            creation_timestamp: new Date().toISOString(),
            waterfall_id: waterfallId,
            waterfall_item_id: "",
            concept: WaterfallItemConceptEnum.CASH_TRAPPING,
            priority: maxPriority + 1,
            processing_order: maxOrder + 1,
        };

        const updatedWaterfalls = localWaterfalls.map(w =>
            w.id === waterfallId
                ? {
                    ...w,
                    waterfall_payment_priority_rows: [
                        ...(w.waterfall_payment_priority_rows || []),
                        newRow
                    ]
                }
                : w
        );

        this.setState(state => ({
            localWaterfalls: updatedWaterfalls,
            nextRowId: state.nextRowId + 1
        }));

        this.registerPriorityRowInputs?.(newRow);
    }


    @autobind
    handleDeleteWaterfall(waterfall_id: UUID) {
        this.processFetchRequest(
            getRequest_delete_waterfall(waterfall_id),
            () => {
                this.props.displayMessage(createSuccessFeedbackMessage(LabelTextMessageTypes.WATERFALL_DELETED));
                this.props.loadData([ApiEndPoints.WATERFALL, ApiEndPoints.VARIABLE]);
            }
        );
    }

    @autobind
    handleDeletePaymentPriorityRow(waterfall_payment_priority_row_id: UUID) {
        this.processFetchRequest(
            getRequest_delete_waterfall_payment_priority_row(waterfall_payment_priority_row_id),
            () => {
                this.props.loadData([ApiEndPoints.WATERFALL, ApiEndPoints.VARIABLE]);
            }
        );
    }

    @autobind
    handleDeleteAvailableFundsRow(waterfall_available_funds_row_id: UUID) {
        this.processFetchRequest(
            getRequest_delete_waterfall_available_funds_row(waterfall_available_funds_row_id),
            () => {
                this.props.loadData([ApiEndPoints.WATERFALL, ApiEndPoints.VARIABLE]);
            }
        );
    }


    @autobind
    handleChangePaymentPriority(rowId: string, newPriority: number) {
        const {localWaterfalls} = this.state;

        const wfIndex = localWaterfalls.findIndex(wf =>
            (wf.waterfall_payment_priority_rows ?? []).some(r => r.id === rowId)
        );
        if (wfIndex === -1) return;

        const waterfall = localWaterfalls[wfIndex];
        const rows = [...(waterfall.waterfall_payment_priority_rows ?? [])];
        const index = rows.findIndex(r => r.id === rowId);
        if (index === -1) return;

        rows[index] = {...rows[index], priority: Math.max(1, newPriority)};

        const updatedWaterfalls = localWaterfalls.map((wf, idx) =>
            idx === wfIndex
                ? {...wf, waterfall_payment_priority_rows: rows}
                : wf
        );

        this.setState({localWaterfalls: updatedWaterfalls}, () => {
            const updatedRow = rows[index];

            this.processFetchRequest(
                getRequest_update_waterfall_payment_priority_row(updatedRow),
                () => {

                    this.props.loadData([ApiEndPoints.WATERFALL, ApiEndPoints.VARIABLE]);
                }
            );
        });
    }

    @autobind
    openEditWaterfallModal(waterfall: Waterfall) {
        this.setState({
            showEditWaterfallModal: true,
            editingWaterfall: waterfall,
            editingWaterfallValid: true,
        });
    }

    @autobind
    closeEditWaterfallModal() {
        this.setState({
            showEditWaterfallModal: false,
            editingWaterfall: undefined,
            editingWaterfallValid: true,
        });
    }

    @autobind
    handleWaterfallEditModalSubmit(waterfallInput: Partial<Waterfall>) {
        const {editingWaterfall} = this.state;
        if (!editingWaterfall) return;
        const updatedWaterfall = {...editingWaterfall, ...waterfallInput};

        this.processFetchRequest(
            getRequest_update_waterfall(updatedWaterfall),
            (updated: Waterfall) => {
                this.setState(state => ({
                    localWaterfalls: state.localWaterfalls.map(w =>
                        w.id === editingWaterfall.id
                            ? {
                                ...w,
                                ...updated,
                                waterfall_available_funds_rows: updated.waterfall_available_funds_rows ?? w.waterfall_available_funds_rows,
                                waterfall_payment_priority_rows: updated.waterfall_payment_priority_rows ?? w.waterfall_payment_priority_rows
                            }
                            : w
                    )
                }));
                this.props.loadData([ApiEndPoints.WATERFALL, ApiEndPoints.VARIABLE]);
            }
        );

        this.closeEditWaterfallModal();
    }


    @autobind
    async handleAddFundsRow(waterfallId: string) {
        const {localWaterfalls, nextRowId} = this.state;
        const parentWaterfall = localWaterfalls.find((w) => w.id === waterfallId);
        if (!parentWaterfall) return;

        const tempId = `newrow-${nextRowId}`;
        const orders = parentWaterfall.waterfall_available_funds_rows?.map(r => r.processing_order ?? 0) || [];
        const maxOrder = orders.length > 0 ? Math.max(...orders) : -1;
        const processing_order = maxOrder + 1;

        const newRow: WaterfallAvailableFundsRow = {
            id: tempId,
            creation_timestamp: new Date().toISOString(),
            waterfall_id: waterfallId,
            fund_variable_id: "",
            processing_order: processing_order
        };

        const updatedWaterfalls = localWaterfalls.map((w) =>
            w.id === waterfallId
                ? {
                    ...w,
                    waterfall_available_funds_rows: [
                        ...(w.waterfall_available_funds_rows || []),
                        newRow
                    ]
                }
                : w
        );

        this.setState(state => ({
            localWaterfalls: updatedWaterfalls,
            nextRowId: state.nextRowId + 1
        }));


        this.registerRowInputs?.(newRow);
    }


    @autobind
    async handleAddWaterfall() {
        const {localWaterfalls} = this.state;

        const baseName = "Waterfall";
        const regex = new RegExp(`^${baseName}(\\s(\\d+))?$`);
        let maxNum = 0;

        localWaterfalls.forEach(wf => {
            const m = (wf.name || "").match(regex);
            if (m) {
                const num = m[2] ? parseInt(m[2], 10) : 1;
                if (num > maxNum) maxNum = num;
            }
        });

        const newWaterfallNumber = maxNum + 1;
        const newWaterfallName = `${baseName} ${newWaterfallNumber}`;

        if (!this.props.transaction?.id) {
            return;
        }
        const newWaterfallPayload: Omit<Waterfall, "id"> = {
            name: newWaterfallName,
            description: "",
            processing_order: localWaterfalls.length,
            waterfall_available_funds_rows: [],
            waterfall_payment_priority_rows: [],
            available_funds_variable_id: "",
            remaining_funds_variable_id: "",
            creation_timestamp: new Date().toISOString(),
            trigger_condition: TriggerConditionEnum.RUN_IF,
            transaction_id: this.props.transaction.id,
            waterfall_processable_type: WaterfallProcessableTypeEnum.WATERFALL,
            trigger_variable_id: undefined
        };

        this.processFetchRequest(
            getRequest_create_waterfall(newWaterfallPayload as Waterfall),
            (createdWaterfall: Waterfall) => {
                if (!createdWaterfall?.id) return;

                this.setState(state => ({
                    localWaterfalls: [...state.localWaterfalls, createdWaterfall],
                    nextWaterfallId: state.nextWaterfallId + 1
                }));

                this.props.loadData?.([ApiEndPoints.WATERFALL, ApiEndPoints.VARIABLE]);
                this.props.displayMessage(createSuccessFeedbackMessage(LabelTextMessageTypes.WATERFALL_CREATED));
                this.props.loadData([ApiEndPoints.WATERFALL, ApiEndPoints.VARIABLE]);
            }
        );
    }

    handleDropVariableToFundsRow = (waterfallId: string, fundsRowId: string, variableId: string) => {
        const {localWaterfalls, rowInputStateIds} = this.state;
        const parentWaterfall = localWaterfalls.find((w) => w.id === waterfallId);
        if (!parentWaterfall) return;

        const rowIndex = parentWaterfall.waterfall_available_funds_rows.findIndex((r) => r.id === fundsRowId);
        if (rowIndex === -1) return;

        const updatedWaterfalls = localWaterfalls.map((w) =>
            w.id === waterfallId
                ? {
                    ...w,
                    waterfall_available_funds_rows: w.waterfall_available_funds_rows.map((row) =>
                        row.id === fundsRowId ? {...row, fund_variable_id: variableId} : row
                    )
                }
                : w
        );

        const ids = rowInputStateIds[fundsRowId];
        if (ids && typeof ids.variableId === "number") {
            this.rowInputStateControl.updateValue(ids.variableId, variableId);
        }

        this.setState({localWaterfalls: updatedWaterfalls}, () => {
            const row = updatedWaterfalls
                .find((w) => w.id === waterfallId)
                ?.waterfall_available_funds_rows.find((r) => r.id === fundsRowId);


            if (row && row.fund_variable_id) {
                const isNew = fundsRowId.startsWith("newrow-");
                if (isNew) {
                    const {id, ...backendRow} = row;
                    this.processFetchRequest(getRequest_create_waterfall_available_funds_row(backendRow), (createdRow: WaterfallAvailableFundsRow) => {
                        const newLocalWaterfalls = this.state.localWaterfalls.map((w) =>
                            w.id === waterfallId
                                ? {
                                    ...w,
                                    waterfall_available_funds_rows: w.waterfall_available_funds_rows.map((r) =>
                                        r.id === fundsRowId ? createdRow : r
                                    )
                                }
                                : w
                        );
                        this.setState({localWaterfalls: newLocalWaterfalls});
                        this.props.loadData([ApiEndPoints.WATERFALL, ApiEndPoints.VARIABLE]);
                    });
                } else {
                    this.processFetchRequest(getRequest_update_waterfall_available_funds_row(row), (updatedRow: WaterfallAvailableFundsRow) => {
                        const newLocalWaterfalls = this.state.localWaterfalls.map((w) =>
                            w.id === waterfallId ? {
                                ...w,
                                waterfall_available_funds_rows: w.waterfall_available_funds_rows.map((r) => (r.id === fundsRowId ? updatedRow : r))
                            } : w
                        );
                        this.setState({localWaterfalls: newLocalWaterfalls});
                        this.props.loadData([ApiEndPoints.WATERFALL, ApiEndPoints.VARIABLE]);
                    });
                }
            }
        });
    };

    handleDropVariableToNewFundsRow = (waterfallId: string, variableId: string) => {
        this.handleAddFundsRow(waterfallId);
        setTimeout(() => {
            const waterfall = this.state.localWaterfalls.find((w) => w.id === waterfallId);
            if (!waterfall) return;
            const lastRow = [...waterfall.waterfall_available_funds_rows].reverse().find((r) => r.id.startsWith("newrow-"));
            if (lastRow) this.handleDropVariableToFundsRow(waterfallId, lastRow.id, variableId);
        });
    };

    @autobind
    handleDropWaterfallItemToRow(waterfallId: string, rowId: string, itemId: string) {
        const {localWaterfalls} = this.state;

        const parentWaterfall = localWaterfalls.find((w) => w.id === waterfallId);
        if (!parentWaterfall) return;

        const rowIndex = parentWaterfall.waterfall_payment_priority_rows.findIndex((r) => r.id === rowId);
        if (rowIndex === -1) return;

        const updatedWaterfalls = localWaterfalls.map((w) =>
            w.id === waterfallId
                ? {
                    ...w,
                    waterfall_payment_priority_rows: w.waterfall_payment_priority_rows.map((r) =>
                        r.id === rowId ? {...r, waterfall_item_id: itemId} : r
                    ),
                }
                : w
        );

        this.setState({localWaterfalls: updatedWaterfalls}, () => {
            const inputIds = this.state.rowInputStateIds[rowId];
            if (
                inputIds &&
                "waterfallItemId" in inputIds &&
                typeof inputIds.waterfallItemId === "number"
            ) {
                this.rowInputStateControl.updateValue(inputIds.waterfallItemId, itemId);
            }

            const row = updatedWaterfalls
                .find((w) => w.id === waterfallId)!
                .waterfall_payment_priority_rows.find((r) => r.id === rowId)!;


            const isNew = row.id.startsWith("newpriority-");
            if (isNew) {
                const {id, ...backendRow} = row;
                this.processFetchRequest(
                    getRequest_create_waterfall_payment_priority_row(backendRow),
                    (createdRow: WaterfallPaymentPriorityRow) => {
                        const newLocalWaterfalls = this.state.localWaterfalls.map((w) =>
                            w.id === waterfallId
                                ? {
                                    ...w,
                                    waterfall_payment_priority_rows: w.waterfall_payment_priority_rows.map((r) =>
                                        r.id === rowId ? createdRow : r
                                    ),
                                }
                                : w
                        );
                        this.setState({localWaterfalls: newLocalWaterfalls});
                        this.props.loadData([ApiEndPoints.WATERFALL, ApiEndPoints.VARIABLE]);
                    }
                );
            } else {
                this.processFetchRequest(
                    getRequest_update_waterfall_payment_priority_row({...row, waterfall_item_id: itemId}),
                    (updatedRow: WaterfallPaymentPriorityRow) => {
                        const newLocalWaterfalls = this.state.localWaterfalls.map((w) =>
                            w.id === waterfallId
                                ? {
                                    ...w,
                                    waterfall_payment_priority_rows: w.waterfall_payment_priority_rows.map((r) =>
                                        r.id === rowId ? updatedRow : r
                                    ),
                                }
                                : w
                        );
                        this.setState({localWaterfalls: newLocalWaterfalls});
                        this.props.loadData([ApiEndPoints.WATERFALL, ApiEndPoints.VARIABLE]);
                    }
                );
            }
        });
    }


    componentDidMount() {
        window.addEventListener("trigger-variable-dropped-back", this.handleTriggerVariableDroppedBack as any);
        window.addEventListener("waterfall-item-dropped-back", this.handleWaterfallItemDroppedBack as any);
    }

    componentWillUnmount() {
        window.removeEventListener("trigger-variable-dropped-back", this.handleTriggerVariableDroppedBack as any);
        window.removeEventListener("waterfall-item-dropped-back", this.handleWaterfallItemDroppedBack as any);
    }

    handleTriggerVariableDroppedBack = (e: CustomEvent<{ variable: string; triggerId: string }>) => {
        const {triggerId} = e.detail;
        this.handleChangeTriggerVariable(triggerId, undefined);
    };

    handleWaterfallItemDroppedBack = (e: CustomEvent<{ wfId: string, rowId: string, section: string }>) => {
        const {wfId, rowId} = e.detail;
        this.handleRemoveWaterfallItemFromRow(wfId, rowId);
    }

    handleChangeTriggerCondition = (waterfallId: string, newValue: TriggerConditionEnum) => {
        const updatedWaterfalls = this.state.localWaterfalls.map((w) =>
            w.id === waterfallId ? {...w, trigger_condition: newValue} : w
        );
        this.setState({localWaterfalls: updatedWaterfalls}, () => {
            const waterfall = updatedWaterfalls.find(w => w.id === waterfallId);
            if (waterfall) {
                this.processFetchRequest(
                    getRequest_update_waterfall({...waterfall, trigger_condition: newValue}),
                    (updated: Waterfall) => {
                        this.setState(state => ({
                            localWaterfalls: state.localWaterfalls.map(w =>
                                w.id === waterfallId
                                    ? {
                                        ...w,
                                        ...updated,
                                        waterfall_available_funds_rows: updated.waterfall_available_funds_rows ?? w.waterfall_available_funds_rows,
                                        waterfall_payment_priority_rows: updated.waterfall_payment_priority_rows ?? w.waterfall_payment_priority_rows
                                    }
                                    : w
                            )
                        }));
                        this.props.loadData([ApiEndPoints.WATERFALL, ApiEndPoints.VARIABLE]);
                    }
                );
            }
        });
    };

    handleChangeTriggerVariable = (waterfallId: UUID, variableId: UUID | undefined) => {
        const updatedWaterfalls = this.state.localWaterfalls.map((w) =>
            w.id === waterfallId ? {...w, trigger_variable_id: variableId} : w
        );
        this.setState({localWaterfalls: updatedWaterfalls}, () => {
            const waterfall = updatedWaterfalls.find(w => w.id === waterfallId);
            if (waterfall) {
                this.processFetchRequest(
                    getRequest_update_waterfall(waterfall),
                    (updated: Waterfall) => {
                        this.setState(state => ({
                            localWaterfalls: state.localWaterfalls.map(w =>
                                w.id === waterfallId
                                    ? {
                                        ...w,
                                        ...updated,
                                        waterfall_available_funds_rows: updated.waterfall_available_funds_rows ?? w.waterfall_available_funds_rows,
                                        waterfall_payment_priority_rows: updated.waterfall_payment_priority_rows ?? w.waterfall_payment_priority_rows
                                    }
                                    : w
                            )
                        }));
                        this.props.loadData([ApiEndPoints.WATERFALL, ApiEndPoints.VARIABLE]);
                    }
                );
            }
        });
    };

    render(): ReactElement {
        const {
            rowInputStateIds,
            showEditWaterfallModal,
            editingWaterfall,
        } = this.state;

        const sorted = this.state.localWaterfalls.slice().sort((a, b) => (a.processing_order ?? 0) - (b.processing_order ?? 0));
        return (
            <div className={styles.tabContainer}>
                <div className={styles.scrollArea}>
                    <Row>
                        <Col xs={12}>
                            <div className={styles.cardStack}>
                                {sorted.map((waterfall) => (
                                    <div key={waterfall.id}>
                                        <RBSCard
                                             header={
                                                 <TabCardHeader
                                                     icon={"🌊"}
                                                     title={
                                                         <span className={styles.cardHeaderTitleWrap}>
                                                             <span className={styles.cardHeaderTitle}>Waterfall: {waterfall.name}</span>
                                                         </span>
                                                     }
                                                     actions={
                                                        <div className={styles.headerActions}>
                                                            <RBSButton
                                                                label="Edit"
                                                                onClick={() => this.setState({
                                                                    showEditWaterfallModal: true,
                                                                    editingWaterfall: waterfall
                                                                })}
                                                                variant={RBSButtonVariants.SECONDARY}
                                                                size={RBSButtonSizes.SMALL}
                                                            />
                                                            <RBSButton
                                                                label="Delete"
                                                                title="Delete Waterfall"
                                                                onClick={() => this.handleDeleteWaterfall(waterfall.id)}
                                                                variant={RBSButtonVariants.DANGER}
                                                                size={RBSButtonSizes.SMALL}
                                                            />
                                                        </div>
                                                     }
                                                 />
                                             }
                                            background={RBSCardVariants.Light}
                                            border={RBSCardVariants.Primary}
                                            header_class_name={styles.cardHeaderPrimary}
                                            body_class_name={styles.cardBodyPanel}
                                            container_class_name={styles.roundedCard}
                                        >
                                            <WaterfallAccordion
                                                user_language={this.props.user_language}
                                                waterfallId={waterfall.id}
                                                onAddPriorityRow={() => this.handleAddPriorityRow(waterfall.id)}
                                                availableFundsRows={[...(waterfall.waterfall_available_funds_rows ?? [])].sort((a, b) => (a.processing_order ?? 0) - (b.processing_order ?? 0))}
                                                paymentPriorityRows={[...(waterfall.waterfall_payment_priority_rows ?? [])].sort((a, b) => (a.processing_order ?? 0) - (b.processing_order ?? 0))}
                                                variables={this.props.variables}
                                                waterfallItems={this.props.waterfall_items}
                                                trigger_condition={waterfall.trigger_condition as TriggerConditionEnum}
                                                trigger_variable_id={waterfall.trigger_variable_id}
                                                onChangeTriggerCondition={(newVal) => this.handleChangeTriggerCondition(waterfall.id, newVal)}
                                                onChangeTriggerVariable={(newVarId) => this.handleChangeTriggerVariable(waterfall.id, newVarId)}
                                                onDropVariableToFundsRow={(fundsRowId, variableId) => this.handleDropVariableToFundsRow(waterfall.id, fundsRowId, variableId)}
                                                onDropVariableToNewFundsRow={(variableId) => this.handleDropVariableToNewFundsRow(waterfall.id, variableId)}
                                                onAddFundsRow={() => this.handleAddFundsRow(waterfall.id)}
                                                onDeleteAvailableFundsRow={this.handleDeleteAvailableFundsRow}
                                                onDeletePaymentPriorityRow={this.handleDeletePaymentPriorityRow}
                                                rowInputStateControl={this.rowInputStateControl}
                                                rowInputStateIds={rowInputStateIds}
                                                onChangePaymentPriority={this.handleChangePaymentPriority}
                                                onDropWaterfallItemToRow={(wfId, rowId, itemId) => this.handleDropWaterfallItemToRow(wfId, rowId, itemId)}
                                                onChangeWaterfallItemConcept={this.handleChangeWaterfallItemConcept}
                                            />
                                        </RBSCard>
                                    </div>
                                ))}
                            </div>
                        </Col>
                    </Row>
                    <div className={styles.toolbarBottom}>
                        <RBSButton
                            label="Add Waterfall"
                            variant={RBSButtonVariants.PRIMARY}
                            size={RBSButtonSizes.LARGE}
                            onClick={this.handleAddWaterfall}
                        />
                    </div>

                    {editingWaterfall && (
                        <WaterfallEditModal
                            show={showEditWaterfallModal}
                            waterfall={editingWaterfall}
                            onSubmit={this.handleWaterfallEditModalSubmit}
                            onClose={this.closeEditWaterfallModal}
                            isValid={this.state.editingWaterfallValid}
                            inputStateControl={this.editingWaterfallInputControl}
                            user_language={this.props.user_language}
                        />
                    )}
                </div>
            </div>
        );
    }
}
